import { z } from "zod";

/**
 * Three categories of memo check the Intake Validator runs. See
 * `services/intake-validator/README.md` for the per-check definitions.
 */
export const intakeValidatorCheckCategorySchema = z.enum([
  "completeness",
  "clarity",
  "consistency",
]);

/**
 * `blocker` failures terminate the run with `validation_status:
 * "incomplete"` and skip the LLM pass entirely — the memo cannot
 * proceed without re-collection. `warning` failures move the status
 * to `"flagged"` but the memo is otherwise structurally complete; an
 * admin or the next chat turn decides whether to act.
 */
export const intakeValidatorCheckSeveritySchema = z.enum([
  "blocker",
  "warning",
]);

/**
 * Single check's outcome from a run. `passed: true` means the memo
 * cleared this check; `reason` carries the human-readable explanation
 * for a failure (null on success). `evidence` is the optional model-
 * quoted snippet from the memo that anchored the verdict — present
 * on LLM-judged checks, omitted on deterministic ones.
 */
export const intakeValidatorCheckOutcomeSchema = z.object({
  id: z.string().min(1),
  category: intakeValidatorCheckCategorySchema,
  name: z.string().min(1),
  severity: intakeValidatorCheckSeveritySchema,
  passed: z.boolean(),
  reason: z.string().nullable(),
  evidence: z.string().optional(),
});

export type IntakeValidatorCheckOutcome = z.infer<
  typeof intakeValidatorCheckOutcomeSchema
>;

/**
 * Pre-computed roll-up of `checks`. Carried alongside the array so
 * downstream consumers (admin-ui memo detail, the re-engagement
 * signal in `loadConsultantContext`) don't each re-derive it.
 *
 * `blockerCount` is the count of failed blockers specifically — if
 * non-zero the run short-circuited and `flagged`/`validated` are
 * never reachable on this artifact.
 */
export const intakeValidatorReportSummarySchema = z.object({
  passedCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  blockerCount: z.number().int().nonnegative(),
});

export const intakeValidatorReportSchema = z.object({
  checks: z.array(intakeValidatorCheckOutcomeSchema),
  summary: intakeValidatorReportSummarySchema,
});

export type IntakeValidatorReport = z.infer<typeof intakeValidatorReportSchema>;

/**
 * The Intake Validator's run summary. Lives in
 * `jobs.workerResult.artifacts[0]` once the orchestrator persists the
 * agent's `run_complete` payload via `applyAgentOutput` (see
 * `services/orchestrator/src/routes/callbacks.ts`).
 *
 * The agent-type is `"intake-validator"` (hyphen) so the orchestrator's
 * validator-hop check (`agent.agentType.endsWith("_validator")`) treats
 * this dispatch as a worker, not a paired validator hop. Job state
 * transitions through `running → completed`; there is no validator
 * hop after this run.
 *
 * Used by both the agent service (`services/intake-validator/`) and
 * the orchestrator's `applyIntakeValidatorOutcome` so the wire
 * contract is single-sourced.
 *
 * Excludes the run-level metadata that's already on `AgentOutput`
 * itself (errors, callSummary, durationSeconds) — those live on the
 * envelope, not in the artifact.
 */
export const intakeValidatorArtifactSchema = z.object({
  /** Discriminator carried for forward-compat — keeps room for the
   *  future `solution-validator` artifact in the same column. */
  kind: z.literal("memo_validation"),
  /** The `problem_memos.id` the validator just judged. */
  memoId: z.string().uuid(),
  validationStatus: z.enum(["validated", "incomplete", "flagged"]),
  /** 0.00–1.00. `passedCount / totalChecks`. */
  validationScore: z.number().min(0).max(1),
  report: intakeValidatorReportSchema,
  /**
   * Forensic snapshot of the `agent_config!A:B` rows that drove this
   * run. Mirrors the pattern in
   * `packages/shared/src/schemas/validator-artifacts.ts` —
   * thresholds + model id frozen so a later audit can interpret the
   * verdict against the templating values in force at the time.
   */
  agentConfigSnapshot: z.record(z.union([z.string(), z.number()])),
  /**
   * Git SHA of the seed file
   * `packages/prompts/src/intake-validator/default.md`
   * at run time, or `"dev"` when running from an uncommitted tree.
   * The seed is the source for `agent_prompts.intake-validator.default`
   * — the live prompt that drove the run is in
   * `jobs.promptsSnapshot["default"]`. This SHA is the seed-file
   * provenance, not the live prompt's.
   */
  promptVersion: z.string().min(1),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime(),
});

export type IntakeValidatorArtifact = z.infer<
  typeof intakeValidatorArtifactSchema
>;
